import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  Button, Checkbox, MessageBar, MessageBarBody, Spinner, Text,
} from '@fluentui/react-components';
import type { ComplianceTask, FilingDocument } from '../../types';
import {
  ApiError,
  previewClientUpdateWithAttachments,
  useSendClientUpdateWithAttachments,
  type ClientLanguage,
  type SendUpdatePreview,
} from '../../services/complianceApi';

// The languages a client can be written in, and the reasons the server gives. An unexpected value from the server renders
// as the safe neighbour ("English" / "not connected"), never as a raw key.
const CLIENT_LANGUAGES: readonly ClientLanguage[] = ['en', 'es', 'pt'];
const LANGUAGE_SOURCES: readonly string[] = ['crm', 'crm-blank', 'crm-invalid', 'crm-unavailable', 'not-connected'];

function languageLine(
  t: (key: string, options?: Record<string, string>) => string, key: string,
  language: ClientLanguage | undefined, source: string | undefined,
): string {
  return t(`taskDetailPage.sendUpdate.language.${key}`, {
    language: t(`taskDetailPage.sendUpdate.language.names.${language && CLIENT_LANGUAGES.includes(language) ? language : 'en'}`),
    why: t(`taskDetailPage.sendUpdate.language.source.${source && LANGUAGE_SOURCES.includes(source) ? source : 'not-connected'}`),
  });
}

interface Props {
  task: ComplianceTask;
  initialObligationId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired after the update actually sent (mutation resolved) — the host page toasts on it. */
  onSent?: () => void;
}

/** Derive a display label for an attachment doc kind. */
function kindLabel(kind: string, t: (key: string) => string): string {
  return kind === 'payment-confirmation'
    ? t('taskDetailPage.sendUpdate.kindPaymentConfirmation')
    : t('taskDetailPage.sendUpdate.kindReturn');
}

/**
 * The selection key for a filing attachment is its unique blobName. Keying by
 * "{obligationId}:{kind}" collided when a filing held two docs of the same kind
 * (e.g. two returns / multiple POS statements) — toggling one toggled all of them.
 */
function attachKey(doc: FilingDocument): string {
  return doc.blobName;
}

export default function SendClientUpdateDialog({ task, initialObligationId, open, onOpenChange, onSent }: Props) {
  const { t } = useTranslation();
  const filed = task.filings.filter((f) => !!f.filedAt);

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialObligationId ? [initialObligationId] : filed.filter((f) => !f.sentToClientAt).map((f) => f.obligationId)),
  );

  // Attachment selection: default all-checked for each filing's attachments.
  // Set<"{obligationId}:{kind}">
  const [selectedAttachKeys, setSelectedAttachKeys] = useState<Set<string>>(() => {
    const keys = new Set<string>();
    filed.forEach((f) => {
      (f.attachments ?? []).forEach((doc) => keys.add(attachKey(doc)));
    });
    return keys;
  });

  const [preview, setPreview] = useState<SendUpdatePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const send = useSendClientUpdateWithAttachments(task.id, task.clientId);

  // Re-seed obligation selection + attachment selection whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    const newSelected = new Set(
      initialObligationId
        ? [initialObligationId]
        : filed.filter((f) => !f.sentToClientAt).map((f) => f.obligationId),
    );
    setSelected(newSelected);

    // Default all attachments checked for the newly-selected filings.
    const keys = new Set<string>();
    filed
      .filter((f) => newSelected.has(f.obligationId))
      .forEach((f) => {
        (f.attachments ?? []).forEach((doc) => keys.add(attachKey(doc)));
      });
    setSelectedAttachKeys(keys);
  }, [open, initialObligationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-preview whenever selection or attachment selection changes.
  const selectedKey = [...selected].sort().join(',');
  const attachKeyStr = [...selectedAttachKeys].sort().join(',');
  useEffect(() => {
    if (!open) return;
    if (selected.size === 0) { setPreview(null); return; }
    void doPreview([...selected], [...selectedAttachKeys]);
  }, [open, selectedKey, attachKeyStr]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleObligation(id: string) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) {
        n.delete(id);
        // Also deselect all attachments for this obligation.
        setSelectedAttachKeys((prevKeys) => {
          const newKeys = new Set(prevKeys);
          task.filings
            .find((f) => f.obligationId === id)
            ?.attachments?.forEach((doc) => newKeys.delete(attachKey(doc)));
          return newKeys;
        });
      } else {
        n.add(id);
        // Auto-check all attachments for the newly-selected obligation.
        const filing = task.filings.find((f) => f.obligationId === id);
        if (filing?.attachments?.length) {
          setSelectedAttachKeys((prevKeys) => {
            const newKeys = new Set(prevKeys);
            filing.attachments!.forEach((doc) => newKeys.add(attachKey(doc)));
            return newKeys;
          });
        }
      }
      return n;
    });
  }

  function toggleAttachment(key: string) {
    setSelectedAttachKeys((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  }

  async function doPreview(ids: string[] = [...selected], attachKeys: string[] = [...selectedAttachKeys]) {
    if (ids.length === 0) return;
    setPreviewing(true);
    try {
      setPreview(await previewClientUpdateWithAttachments(task.id, task.clientId, ids, attachKeys));
    } finally {
      setPreviewing(false);
    }
  }

  async function doSend() {
    // Every selected filing is handled by the CRM confirmation Flow — there is nothing to send,
    // and pretending otherwise is the defect this dialog is guarding against.
    if (allSuppressed) return;
    try {
      await send.mutateAsync({ obligationIds: [...selected], attachmentKeys: [...selectedAttachKeys] });
    } catch {
      // Send failed — keep the dialog open; send.isError renders the inline error below.
      return;
    }
    onOpenChange(false);
    onSent?.();
  }

  // CRM task-sync suppression. When mark-filed synced outbound and completed the Dataverse task,
  // that task's own Flow confirms the filing to the client — so the app deliberately sends
  // nothing. The preview reports it, and we surface it here rather than letting Send look like
  // it worked (the production silent failure).
  const suppressedFilings = preview?.suppressedFilings ?? [];
  const suppressedNames = suppressedFilings.map((s) => s.obligationDisplayName).join(', ');
  const allSuppressed = preview?.outcome === 'nothing_sent_all_suppressed';
  const someSuppressed = preview?.outcome === 'partially_sent';

  // The backend answers 409 when suppression landed between preview and send (mark-filed on
  // another tab, a late sync). Name that case rather than showing a generic "try again", which
  // would send the user round a loop that can never succeed.
  const sendBlockedBySuppression =
    send.error instanceof ApiError && send.error.code === 'nothing_sent_all_suppressed';

  // Filings that have attachment docs and are currently selected (show checkboxes for them).
  const filedWithAttachments = task.filings.filter(
    (f) => !!f.filedAt && selected.has(f.obligationId) && (f.attachments?.length ?? 0) > 0,
  );

  return (
    <Dialog open={open} onOpenChange={(_, d) => onOpenChange(d.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('taskDetailPage.sendUpdate.title')}</DialogTitle>
          <DialogContent>
            {task.filings.map((f) => (
              <Checkbox
                key={f.obligationId}
                label={`${f.obligationDisplayName}${f.sentToClientAt ? ` · ${t('taskDetailPage.sendUpdate.alreadySent')}` : ''}`}
                disabled={!f.filedAt}
                checked={selected.has(f.obligationId)}
                onChange={() => toggleObligation(f.obligationId)}
              />
            ))}

            {/* Attachment checkboxes (item P) — shown for selected filed obligations that have docs */}
            {filedWithAttachments.length > 0 && (
              <div style={{ marginTop: 12, paddingLeft: 16 }}>
                <Text size={200} weight="semibold">{t('taskDetailPage.sendUpdate.attachmentsToInclude')}</Text>
                {filedWithAttachments.map((f) =>
                  (f.attachments ?? []).map((doc) => {
                    const key = attachKey(doc);
                    return (
                      <Checkbox
                        key={key}
                        label={`${f.obligationDisplayName} — ${kindLabel(doc.kind, t)} (${doc.fileName})`}
                        checked={selectedAttachKeys.has(key)}
                        onChange={() => toggleAttachment(key)}
                      />
                    );
                  }),
                )}
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <Button
                appearance="secondary"
                disabled={selected.size === 0 || previewing}
                onClick={() => void doPreview()}
              >
                {previewing ? <Spinner size="tiny" /> : t('taskDetailPage.sendUpdate.preview')}
              </Button>
            </div>
            {(allSuppressed || someSuppressed) && (
              <div style={{ marginTop: 12 }}>
                <MessageBar intent={allSuppressed ? 'warning' : 'info'}>
                  <MessageBarBody>
                    {t(
                      allSuppressed
                        ? 'taskDetailPage.sendUpdate.allSuppressed'
                        : 'taskDetailPage.sendUpdate.someSuppressed',
                      { names: suppressedNames },
                    )}
                  </MessageBarBody>
                </MessageBar>
              </div>
            )}
            {preview && (
              <div style={{ marginTop: 12 }}>
                <Text weight="semibold">
                  {preview.testRedirect ? t('taskDetailPage.sendUpdate.testRedirect') : t('taskDetailPage.sendUpdate.to')}{' '}
                  {preview.emailTo ?? t('taskDetailPage.sendUpdate.noEmail')}{preview.smsTo ? ` / ${preview.smsTo}` : ''}
                </Text>
                {preview.emailFrom && (
                  // Shown because staff sign these emails personally: seeing the mailbox next
                  // to the signature is what makes a mismatch visible before Send, not after.
                  <Text block>
                    {t('taskDetailPage.sendUpdate.from', { email: preview.emailFrom })}
                    {preview.senderName ? ` ${t('taskDetailPage.sendUpdate.signedAs', { name: preview.senderName })}` : ''}
                  </Text>
                )}
                {/* The client is written to in their CRM language (en / es / pt). Staff see which, and why, before Send. The
                    email is the company's language and the text the person's (DP-CLIENT-LANG-1), so when they differ, both show. */}
                {preview.smsLanguage === undefined
                  || (preview.smsLanguage === preview.language && preview.smsLanguageSource === preview.languageSource) ? (
                  <Text block data-testid="send-update-language">
                    {languageLine(t, 'line', preview.language, preview.languageSource)}
                  </Text>
                ) : (
                  <>
                    <Text block data-testid="send-update-language">
                      {languageLine(t, 'emailLine', preview.language, preview.languageSource)}
                    </Text>
                    <Text block data-testid="send-update-sms-language">
                      {languageLine(t, 'smsLine', preview.smsLanguage, preview.smsLanguageSource)}
                    </Text>
                  </>
                )}
                <Text block>{preview.subject}</Text>
                <div dangerouslySetInnerHTML={{ __html: preview.html }} />
                <Text block>{preview.sms}</Text>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {send.isError && (
              <MessageBar intent={sendBlockedBySuppression ? 'warning' : 'error'}>
                <MessageBarBody>
                  {t(sendBlockedBySuppression
                    ? 'taskDetailPage.sendUpdate.allSuppressedOnSend'
                    : 'taskDetailPage.sendUpdate.sendFailed')}
                </MessageBarBody>
              </MessageBar>
            )}
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>{t('taskDetailPage.sendUpdate.cancel')}</Button>
            <Button appearance="primary" disabled={!preview || allSuppressed || send.isPending} onClick={() => void doSend()}>
              {send.isPending ? <Spinner size="tiny" /> : t('taskDetailPage.sendUpdate.send')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
